/**
 * Avatar.tsx — 用户 / 操作员头像渐变方块或圆
 *
 * 逐字搬自原型 design/Novel-admin/src/primitives.jsx 的 Avatar：
 * - seed（字符串）自动 hash→色相，或显式 hue 覆盖；
 * - 形状默认圆（radius=999），传 radius 改圆角方块；
 * - size 像素；fontSize 默认 max(9, round(size*0.4))；
 * - 占位字母经 children ?? text 渲染。
 * 默认值（size=28 / mult=47 / idx=4 / radius=999）与各调用方 1:1 保真。
 */

import type { CSSProperties, ReactNode } from 'react';
import { avatarBg, avatarHue } from '~/ui/visual-helpers';

interface AvatarProps {
  /** hash 着色的种子字符串（未显式传 hue 时使用） */
  seed?: string | null;
  /** 显式色相，优先于 seed 派生 */
  hue?: number;
  /** 像素尺寸（宽=高），默认 28 */
  size?: number;
  /** 圆角；默认 999（圆形），传数值变圆角方块 */
  radius?: number;
  /** 占位文字（children 缺省时使用） */
  text?: ReactNode;
  /** 占位内容（优先于 text） */
  children?: ReactNode;
  /** avatarHue 乘数，默认 47 */
  mult?: number;
  /** avatarHue 取字符位，默认 4 */
  idx?: number;
  /** 字号；默认 max(9, round(size*0.4)) */
  fontSize?: number;
  /** 透传外层样式覆盖 */
  style?: CSSProperties;
}

export function Avatar({
  seed,
  hue,
  size = 28,
  radius,
  text,
  children,
  mult = 47,
  idx = 4,
  fontSize,
  style,
}: AvatarProps) {
  const h = hue !== undefined ? hue : avatarHue(seed, { idx, mult });
  const fs = fontSize !== undefined ? fontSize : Math.max(9, Math.round(size * 0.4));
  return (
    <div
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: radius !== undefined ? radius : 999,
        background: avatarBg(h),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontWeight: 600,
        fontSize: fs,
        ...style,
      }}
    >
      {children ?? text}
    </div>
  );
}
